<script setup lang="ts">
import { computed, ref } from 'vue';

import { useClipboard } from '@vueuse/core';

import fileIcon from '@/assets/icons/file.svg';
import supportIcon from '@/assets/icons/support.svg';
import { useErrorModal } from '@/composables/useErrorModal';
import {
  type StatusDisplayedDetailsItem,
  useTransactionDetails
} from '@/composables/useTransactionDetails';
import { useViewBreakpoints } from '@/composables/useViewBreakpoints';
import { formatTimestampDate } from '@/helpers/formatters';
import { getTokens } from '@/helpers/transactions';
import { DetailsItemType, TransactionStatusOTC } from '@/references/axios/transactions/types';
import { useI18n } from '@/references/i18n';
import { settings } from '@/references/settings/globals';

import TransactionDetailsHeader from '@/components/transaction-details/TransactionDetailsHeader.vue';
import TransactionStatusDetailsItem from '@/components/transaction-details/TransactionStatusDetailsItem.vue';
import UiAmounts from '@/components/ui/UiAmounts.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiExpandable from '@/components/ui/UiExpandable.vue';
import type { UiListPairGroup, UiListPairItem } from '@/components/ui/UiListPair.types';
import UiListPair from '@/components/ui/UiListPair.vue';
import UiSpinner from '@/components/ui/UiSpinner.vue';
import UiText from '@/components/ui/UiText.vue';
import UiTitledContainer from '@/components/ui/UiTitledContainer.vue';

const DETAILS_FEE_ITEM_ID = 'fee-button';
const DETAILS_CHAT_ITEM_ID = 'chat-button';
const DETAILS_RECEIPT_ITEM_ID = 'receipt-button';
const DETAILS_COPY_ITEM_ID = 'copy-button';

interface Props {
  underlay?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  underlay: false
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();
const {
  transactionId,
  title,
  description,
  transaction,
  iconSrc,
  fallbackText,
  fallbackColor,
  fallbackSrc,
  loaderColor,
  loading,
  failed,
  topAmount,
  topAmountMuted,
  topAmountHighlighted,
  topAmountBordered,
  topAmountStrikeThrough,
  topAmountWithSkeleton,
  bottomAmount,
  bottomAmountMuted,
  bottomAmountToken,
  bottomAmountWithSkeleton,
  bottomAmountStrikeThrough,
  details,
  statusDetails,
  longPending,
  buttons,
  openStatusModal,
  openFeeModal,
  openChat,
  downloadReceipt,
  failReason
} = useTransactionDetails();
const { copy, isSupported } = useClipboard({ legacy: true });
const { notify } = useErrorModal();
const { lessThanXL } = useViewBreakpoints();

const collapsedStatusDetails = ref<boolean>(true);

const lastStatusDetailsItem = computed((): StatusDisplayedDetailsItem | null => {
  if (!statusDetails.value.length) {
    return null;
  }

  return statusDetails.value[statusDetails.value.length - 1];
});

const previousStatusDetailsItems = computed((): StatusDisplayedDetailsItem[] => {
  if (statusDetails.value.length < 2) {
    return [];
  }

  return statusDetails.value.slice(0, -1);
});

const controlItems = computed((): UiListPairGroup | undefined => {
  const items = new Array<UiListPairItem>();

  if (
    settings.statementsEnabled &&
    transactionId.value !== undefined &&
    [TransactionStatusOTC.Processing, TransactionStatusOTC.Completed].includes(
      transaction.value?.status as TransactionStatusOTC
    )
  ) {
    items.push({
      id: DETAILS_RECEIPT_ITEM_ID,
      label: t('activity.controlItems.receipt'),
      icon: fileIcon,
      clickable: true,
      value: t('activity.controlItems.download')
    });
  }

  if (settings.chatEnabled) {
    items.push({
      id: DETAILS_CHAT_ITEM_ID,
      label: t('activity.controlItems.getHelp'),
      icon: supportIcon,
      clickable: true,
      value: t('activity.controlItems.supportChat')
    });
  }

  return items.length === 0 ? undefined : { items };
});

const processedDetails = computed((): UiListPairGroup[] => {
  const groups: UiListPairGroup[] = [];
  let bufferGroup: UiListPairGroup = { items: [] };

  if (controlItems.value !== undefined) {
    groups.push(controlItems.value);
  }

  details.value.forEach((detailsItem) => {
    const processedItem: UiListPairItem = {
      label: detailsItem.name,
      value: detailsItem.value,
      image: detailsItem.iconUrl,
      link: detailsItem.link,
      blink: detailsItem.skeleton,
      vertical: detailsItem.vertical,
      coloredCircleColor: detailsItem.coloredCircleColor,
      coloredCircleIcon: detailsItem.coloredCircleIcon
    };

    if (detailsItem.type === DetailsItemType.Fee) {
      processedItem.clickable = true;
      processedItem.id = DETAILS_FEE_ITEM_ID;
    }

    bufferGroup.items.push(processedItem);

    if (detailsItem.marginBottom) {
      groups.push(bufferGroup);
      bufferGroup = { items: [] };
    }
  });

  if (bufferGroup.items.length) {
    groups.push(bufferGroup);
  }

  return groups;
});

const estimatingText = computed((): string => {
  if (longPending.value) {
    switch (transaction.value?.type) {
      default:
        return t('activity.details.pending.estimatingLongDescriptionTransaction');
    }
  }

  if (transaction.value?.expectedSuccessAt != undefined) {
    return t('activity.details.pending.estimatingSoonDescriptionDate', {
      date: formatTimestampDate(transaction.value.expectedSuccessAt)
    });
  }

  return t('activity.details.pending.estimatingSoonDescriptionSeconds');
});

function clickCollapserStatusDetails(): void {
  if (!previousStatusDetailsItems.value.length) {
    return;
  }

  collapsedStatusDetails.value = !collapsedStatusDetails.value;
}

function onItemClick(item: UiListPairItem): void {
  if (item.id === DETAILS_FEE_ITEM_ID) {
    openFeeModal();
  }

  if (item.id === DETAILS_CHAT_ITEM_ID) {
    if (lessThanXL.value) {
      emit('close');
    }

    openChat();
    return;
  }

  if (item.id === DETAILS_RECEIPT_ITEM_ID) {
    downloadReceipt();
    return;
  }

  if (item.id === DETAILS_COPY_ITEM_ID) {
    if (!isSupported.value) {
      notify(t('errors.clipboardCopyNotSupported'));
      return;
    }

    copy(transactionId.value ?? '');
    notify(t('activity.contextButton.copiedTransactionId'));
  }
}
</script>

<template>
  <section class="transaction-details">
    <TransactionDetailsHeader
      class="transaction-details__header"
      :title="title"
      :description="description"
      :src="iconSrc"
      :loading="loading"
      :failed="failed"
      :fallback-text="fallbackText"
      :fallback-color="fallbackColor"
      :fallback-src="fallbackSrc"
      :loader-color="loaderColor"
      :tokens="getTokens(transaction)"
    />
    <UiAmounts
      class="transaction-details__amounts"
      :top-amount="topAmount"
      :top-amount-with-skeleton="topAmountWithSkeleton"
      :top-amount-muted="topAmountMuted"
      :top-amount-highlighted="topAmountHighlighted"
      :top-amount-bordered="topAmountBordered"
      :top-amount-strike-through="topAmountStrikeThrough"
      :bottom-amount="bottomAmount"
      :bottom-amount-muted="bottomAmountMuted"
      :bottom-amount-token="bottomAmountToken"
      :bottom-amount-with-skeleton="bottomAmountWithSkeleton"
      :bottom-amount-strike-through="bottomAmountStrikeThrough"
    />

    <UiTitledContainer
      v-if="longPending || loading"
      :title="t('activity.details.pending.statusTitle')"
      class="transaction-details__status-section"
      :inverted="props.underlay"
    >
      <div class="transaction-details__status-pending">
        <UiSpinner class="transaction-details__status-pending-icon" :size="16" />
        <UiText size="sm" weight="medium">
          {{ t('activity.details.pending.processing') }}
        </UiText>
      </div>
      <UiText size="sm" weight="medium" tag="p" muted>
        {{ estimatingText }}
      </UiText>
    </UiTitledContainer>

    <UiTitledContainer
      v-else-if="failed"
      :title="t('activity.details.pending.statusTitle')"
      class="transaction-details__status-section"
      :inverted="props.underlay"
    >
      <div class="transaction-details__status-failed">
        <UiText class="transaction-details__status-failed-label" size="sm" weight="medium">
          {{ t('activity.details.pending.failed') }}
        </UiText>
        <UiText size="sm" weight="medium" muted>
          {{ failReason }}
        </UiText>
      </div>
    </UiTitledContainer>

    <component
      :is="previousStatusDetailsItems.length ? 'button' : 'div'"
      v-else-if="lastStatusDetailsItem"
      class="transaction-details__status-section"
      :type="previousStatusDetailsItems.length ? 'button' : undefined"
      @click="clickCollapserStatusDetails"
    >
      <UiTitledContainer :inverted="props.underlay" :one-row-height="30">
        <span class="transaction-details__status-list-wrapper">
          <UiExpandable v-if="previousStatusDetailsItems.length">
            <span v-if="!collapsedStatusDetails" class="transaction-details__status-previous-items">
              <TransactionStatusDetailsItem
                v-for="item in previousStatusDetailsItems"
                :key="item.key"
                class="transaction-details__status-previous-item"
                :item="item"
              />
            </span>
          </UiExpandable>
          <TransactionStatusDetailsItem
            class="transaction-details__status-last-item"
            :item="lastStatusDetailsItem"
            @open-status-modal="openStatusModal"
          />
        </span>
      </UiTitledContainer>
    </component>

    <UiListPair
      class="transaction-details__details"
      :inverted="props.underlay"
      :details="processedDetails"
      mod="table"
      @click:item="onItemClick"
    />

    <div v-if="buttons.length" class="transaction-details__buttons">
      <UiButton
        v-for="item in buttons"
        :key="item.key"
        class="transaction-details__button"
        :loading="item.loading"
        :mod="item.mod"
        :disabled="item.disabled"
        type="button"
        @click="item.handler"
      >
        {{ item.title }}
      </UiButton>
    </div>
  </section>
</template>

<style scoped>
.transaction-details {
  display: flex;
  flex-direction: column;
}

.transaction-details__header {
  margin: 0 0 40px;
}

.transaction-details__amounts {
  margin: 0 0 40px;
}

button.transaction-details__status-section {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  text-align: left;
  background-color: transparent;
  border: none;
  border-radius: 0;
  cursor: pointer;
  appearance: none;
  user-select: none;
}

.transaction-details__status-section:not(:last-child) {
  margin: 0 0 24px;
}

.transaction-details__status-pending {
  display: flex;
  align-items: center;
  margin: 0 0 8px;
}

.transaction-details__status-pending-icon {
  flex-shrink: 0;
  margin: 0 8px 0 0;
}

.transaction-details__status-failed {
  display: flex;
  justify-content: space-between;
}

.transaction-details__status-failed-label {
  margin: 0 16px 0 0;
}

.transaction-details__status-list-wrapper {
  display: block;
  margin: -8px 0 0;
}

.transaction-details__status-previous-items {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.transaction-details__status-previous-item {
  padding: 8px 0;
  border-bottom: 1px solid var(--color-main-stroke);
}

.transaction-details__status-last-item {
  padding: 8px 0 0;
}

.transaction-details__details {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

.transaction-details__details:not(:last-child) {
  margin: 0 0 40px;
}

.transaction-details__buttons {
  display: flex;
  justify-content: space-between;
  margin: auto 0 0;
}

@media (max-width: 413px) {
  .transaction-details__buttons {
    flex-direction: column-reverse;
  }
}

.transaction-details__button {
  width: calc(50% - 12px);
}

@media (max-width: 413px) {
  .transaction-details__button {
    width: 100%;
  }
}

@media (max-width: 413px) {
  .transaction-details__button:not(:first-child) {
    margin: 0 0 16px;
  }
}
</style>
